if (!customElements.get('responsive-navigation')) {
  customElements.define('responsive-navigation', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.nav = this.querySelector('nav');
      if (!this.nav) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.desktop = matchMedia('(min-width: 1025px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.animations = new Map();
      this.timers = new Map();
      this.items = [...this.nav.querySelectorAll('.horizontal-list > .menu-parent')];
      this.triggers = [...this.closest('header').querySelectorAll('.nav-toggle')];
      this.nav.id ||= `navigation-${this.closest('header').dataset.sectionId}`;
      this.triggers.forEach((trigger) => {
        trigger.setAttribute('role', 'button');
        trigger.setAttribute('aria-label', this.nav.getAttribute('aria-label'));
        trigger.setAttribute('tabindex', '0');
        trigger.setAttribute('aria-controls', this.nav.id);
        trigger.setAttribute('aria-expanded', 'false');
        trigger.addEventListener('click', () => this.setDrawer(!this.drawerOpen), { signal });
        trigger.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.setDrawer(!this.drawerOpen);
        }, { signal });
      });
      this.nav.querySelectorAll('[data-nav-toggle], [data-nav-child-toggle]').forEach((button, index) => {
        const panel = this.panelFor(button);
        if (!panel) return;
        panel.id ||= `${this.nav.id}-panel-${index}`;
        button.setAttribute('aria-controls', panel.id);
      });
      this.items.forEach((item) => {
        let pointer;
        item.addEventListener('pointermove', (event) => { pointer = { x: event.pageX, y: event.pageY }; }, { signal });
        item.addEventListener('pointerenter', (event) => {
          if (!this.desktop.matches || event.pointerType === 'touch') return;
          this.clearTimers();
          if (item.classList.contains('visible')) return;
          pointer = { x: event.pageX, y: event.pageY };
          let previous = pointer;
          const compare = () => {
            if (Math.hypot(previous.x - pointer.x, previous.y - pointer.y) < 6) this.setItem(item, true);
            else { previous = pointer; this.timers.set(item, setTimeout(compare, 100)); }
          };
          this.timers.set(item, setTimeout(compare, 100));
        }, { signal });
        item.addEventListener('pointerleave', () => {
          if (!this.desktop.matches) return;
          this.clearTimers();
          this.timers.set(item, setTimeout(() => this.resetItems(), 180));
        }, { signal });
      });
      this.nav.addEventListener('click', (event) => this.onClick(event), { signal });
      this.nav.querySelectorAll('.button-localization').forEach((button) => {
        if (button.matches('button')) return;
        button.setAttribute('role', 'button');
        button.setAttribute('tabindex', '0');
        button.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          button.click();
        }, { signal });
      });
      this.nav.addEventListener('keydown', (event) => this.onKeyDown(event), { signal });
      this.nav.addEventListener('focusin', (event) => {
        if (!this.desktop.matches) return;
        const item = event.target.closest('.horizontal-list > .menu-parent');
        if (item && !event.target.closest('[data-nav-toggle]')) this.setItem(item, true);
      }, { signal });
      this.nav.addEventListener('focusout', (event) => {
        if (this.desktop.matches && !this.nav.contains(event.relatedTarget)) this.resetItems();
      }, { signal });
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target) && !this.triggers.some((trigger) => trigger.contains(event.target))) {
          this.setDrawer(false, false);
          this.resetItems();
        }
      }, { signal });
      this.desktop.addEventListener('change', () => this.changeMode(), { signal });
      window.addEventListener('resize', () => this.positionPanels(), { signal });
      document.addEventListener('theme:header-metrics:update', () => this.positionPanels(), { signal });
      this.nav.querySelectorAll('.navigation__menu-content > ul').forEach((list) => list.style.removeProperty('display'));
      this.changeMode();
    }

    disconnectedCallback() {
      this.setDrawer(false, false);
      this.clearTimers();
      this.animations?.forEach((animation) => animation.cancel());
      this.controller?.abort();
      this.controller = null;
    }

    panelFor(button) {
      return button.matches('[data-nav-toggle]')
        ? button.closest('.nav-disclosure')?.querySelector(':scope > .collapsible-content')
        : button.closest('.level-1')?.querySelector(':scope > .sub-children-menu');
    }

    clearTimers() {
      this.timers?.forEach(clearTimeout);
      this.timers?.clear();
    }

    setDrawer(open, restoreFocus = true) {
      if (this.desktop?.matches) open = false;
      const wasOpen = this.drawerOpen;
      this.drawerOpen = open;
      if (open && !wasOpen) this.previousPadding = document.documentElement.style.paddingRight;
      document.documentElement.classList.toggle('nav-open', open);
      if (open) {
        document.documentElement.style.paddingRight = `${Math.max(0, window.innerWidth - document.documentElement.clientWidth)}px`;
      } else if (wasOpen) {
        document.documentElement.style.paddingRight = this.previousPadding || '';
      }
      if (!open) this.nav.querySelectorAll('localization-form').forEach((form) => form.hidePanel?.());
      this.triggers?.forEach((trigger) => {
        trigger.classList.toggle('open', open);
        trigger.setAttribute('aria-expanded', String(open));
      });
      if (!this.desktop?.matches) this.nav.inert = !open;
      if (open) this.nav.querySelector('[data-nav-close]')?.focus({ preventScroll: true });
      else if (wasOpen && restoreFocus) this.triggers?.[0]?.focus({ preventScroll: true });
    }

    setItem(item, open, animate = false) {
      const block = item.querySelector(':scope > .nav-disclosure');
      const panel = block?.querySelector(':scope > .collapsible-content');
      if (!panel) return;
      if (this.desktop.matches && open) {
        this.items.filter((other) => other !== item).forEach((other) => this.setItem(other, false));
      }
      const height = panel.getBoundingClientRect().height;
      item.classList.toggle('visible', this.desktop.matches && open);
      block.classList.toggle('active', open);
      block.querySelector('[data-nav-toggle]')?.setAttribute('aria-expanded', String(open));
      if (!this.desktop.matches && open) {
        panel.querySelectorAll('[data-nav-child-toggle]').forEach((button) => this.setChild(button, true));
      }
      if (!this.desktop.matches && animate) this.animatePanel(panel, open, height);
      if (this.desktop.matches && open) this.positionPanels();
    }

    setChild(button, open, animate = false) {
      const panel = this.panelFor(button);
      const height = panel?.getBoundingClientRect().height;
      const item = button.closest('menu-item');
      item.classList.toggle('is-open', open);
      item.querySelector('a')?.classList.toggle('active', open);
      button.setAttribute('aria-expanded', String(open));
      if (panel && animate && !this.desktop.matches) this.animatePanel(panel, open, height);
    }

    animatePanel(panel, open, height) {
      this.animations.get(panel)?.cancel();
      if (this.reducedMotion.matches) return;
      panel.style.display = 'block';
      panel.style.overflow = 'hidden';
      const animation = panel.animate([
        { height: `${height}px` },
        { height: `${open ? panel.scrollHeight : 0}px` },
      ], { duration: 250, easing: 'ease' });
      this.animations.set(panel, animation);
      const cleanup = () => {
        if (this.animations.get(panel) !== animation) return;
        panel.style.removeProperty('display');
        panel.style.removeProperty('overflow');
        this.animations.delete(panel);
      };
      animation.finished.then(cleanup, cleanup);
    }

    onClick(event) {
      const close = event.target.closest('[data-nav-close]');
      if (close) return this.setDrawer(false);
      const tab = event.target.closest('[data-menu]');
      if (tab) {
        event.preventDefault();
        this.nav.querySelectorAll('.menu-mobile-title [data-menu]').forEach((entry) => entry.classList.toggle('active', entry === tab));
        this.nav.querySelectorAll('.navigation__menu-content > ul').forEach((list) => {
          list.hidden = !list.classList.contains(tab.dataset.menu) && !(tab.dataset.menu === 'categories-list' && list.classList.contains('categories-list-menu-mobile'));
        });
        return;
      }
      const child = event.target.closest('[data-nav-child-toggle]');
      if (child) {
        event.preventDefault();
        this.setChild(child, child.getAttribute('aria-expanded') !== 'true', true);
        return;
      }
      const childLink = event.target.closest('.level-1 > menu-item > a');
      const childToggle = childLink?.parentElement.querySelector('[data-nav-child-toggle]');
      if (childToggle && !this.desktop.matches && this.nav.dataset.actionMobile !== 'true') {
        event.preventDefault();
        this.setChild(childToggle, childToggle.getAttribute('aria-expanded') !== 'true', true);
        return;
      }
      const heading = event.target.closest('.nav-disclosure > .collapsible-heading');
      if (!heading) return;
      const toggle = event.target.closest('[data-nav-toggle]');
      const link = event.target.closest('a');
      if (!toggle && (this.desktop.matches || this.nav.dataset.actionMobile === 'true') && link) return;
      event.preventDefault();
      const item = heading.closest('.level0');
      this.clearTimers();
      this.setItem(item, !item.querySelector(':scope > .nav-disclosure').classList.contains('active'), true);
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        if (event.target.closest('localization-form')?.querySelector('.button-localization.open')) return;
        event.preventDefault();
        if (this.desktop.matches) {
          const item = event.target.closest('.horizontal-list > .menu-parent');
          this.resetItems();
          item?.querySelector('[data-nav-toggle]')?.focus();
        } else this.setDrawer(false);
      }
      if (event.key !== 'Tab' || this.desktop.matches || !this.drawerOpen) return;
      const focusable = [...this.nav.querySelectorAll('a[href], button, [tabindex="0"]')].filter((element) =>
        !element.disabled && element.getClientRects().length && getComputedStyle(element).visibility === 'visible'
      );
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && event.target === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && event.target === last) { event.preventDefault(); first?.focus(); }
    }

    resetItems() {
      this.clearTimers();
      this.animations?.forEach((animation) => animation.cancel());
      this.animations?.clear();
      this.nav.querySelectorAll('.collapsible-content, .sub-children-menu').forEach((panel) => {
        ['display', 'height', 'overflow'].forEach((property) => panel.style.removeProperty(property));
        if (!this.desktop.matches) panel.style.removeProperty('left');
      });
      this.items?.forEach((item) => this.setItem(item, false));
      this.nav.querySelectorAll('[data-nav-child-toggle]').forEach((button) => this.setChild(button, false));
    }

    changeMode() {
      this.setDrawer(false, false);
      this.resetItems();
      this.nav.inert = !this.desktop.matches;
      this.nav.querySelectorAll('.navigation__menu-content > ul').forEach((list) => { list.hidden = !list.classList.contains('horizontal-list'); });
      this.nav.querySelectorAll('.menu-mobile-title [data-menu]').forEach((tab) => tab.classList.toggle('active', tab.dataset.menu === 'horizontal-list'));
      const area = this.desktop.matches ? 'desktop_header' : 'mobile_header';
      this.nav.querySelectorAll('[data-analytics-nav-area=desktop_header], [data-analytics-nav-area=mobile_header]').forEach((link) => {
        link.dataset.analyticsNavArea = area;
        ['analyticsNavId', 'analyticsNavParentId'].forEach((key) => {
          if (link.dataset[key]) link.dataset[key] = link.dataset[key].replace(/^(desktop|mobile)_header/, area);
        });
      });
      this.positionPanels();
      document.dispatchEvent(new CustomEvent('theme:header-metrics:update'));
    }

    positionPanels() {
      if (!this.desktop?.matches) return;
      this.nav.querySelectorAll('.mega-menu-fix-width, .dropdown-menu > .nav-disclosure > .submenu').forEach((panel) => {
        panel.style.removeProperty('left');
        const rect = panel.getBoundingClientRect();
        if (rect.right > window.innerWidth - 30) panel.style.left = `${panel.offsetLeft - (rect.right - window.innerWidth + 30)}px`;
      });
    }
  });
}
